/* eslint-disable camelcase */
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_JOBS_URL;

export const createWallet = createAsyncThunk(
  'payment/createWallet',
  async (
    { token, userId }: { token: string; userId: string | null },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post(
        `${API_URL}/wallet/wallet/create`,
        { userId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getWallets = createAsyncThunk(
  'payment/getWallets',
  async (token: string | null, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/wallet/wallets`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getWalletByUserId = createAsyncThunk(
  'payment/getWalletByUserId',
  async (
    { userId, token }: { userId: string; token: string | null },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.get(`${API_URL}/wallet/wallet/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const topUpWallet = createAsyncThunk(
  'payment/topUpWallet',
  async (
    {
      amount,
      userEmail,
      token,
    }: { token: string; amount: number; userEmail: string | null },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post(
        `${API_URL}/wallet/topup`,
        { amount, userEmail },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const confirmTopUp = createAsyncThunk(
  'payment/confirmTopUp',
  async (
    { reference, token }: { reference: string; token: string | null },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post(
        `${API_URL}/wallet/confirmtopup`,
        { reference },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const releaseFunds = createAsyncThunk(
  'payment/releaseFunds',
  async (
    {
      amount,
      recipient,
      reason,
      token,
    }: {
      amount: number;
      recipient: string;
      reason: string;
      token: string | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post(
        `${API_URL}/wallet/releaseFunds`,
        { amount, recipient, reason },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const finalizeTransfer = createAsyncThunk(
  'payment/finalizeTransfer',
  async (
    {
      transfer_code,
      amount,
      token,
    }: { transfer_code: string; amount: number; token: string | null },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post(
        `${API_URL}/wallet/finalizeTransfer`,
        { transfer_code, amount },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const createRecipient = createAsyncThunk(
  'payment/createRecipient',
  async (
    {
      type,
      name,
      account_number,
      bank_code,
      token,

    }: {
      type: string;
      name: string;
      account_number: string;
      bank_code: string;
      token: string | null;

    },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post(
        `${API_URL}/wallet/createRecipient`,
        {
          type, name, account_number, bank_code,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Fetch All Recipients
export const getAllRecipients = createAsyncThunk(
  'payment/getAllRecipients',
  async (token: string | null, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/wallet/recipients`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.recipients;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Fetch Recipient by ID
export const getRecipientById = createAsyncThunk(
  'payment/getRecipientById',
  async (
    { id, token }: { id: string; token: string | null },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.get(`${API_URL}/wallet/recipients/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.recipient;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);
export const getUserTransaction = createAsyncThunk(
  'payment/getUserTransaction',
  async (
    { user_email, token }: { user_email: string; token: string | null },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.get(
        `${API_URL}/wallet/transaction/${user_email}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const fetchTransactions = createAsyncThunk(
  'payment/fetchTransactions',
  async ({ email, token }: { email: string; token: string }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/wallet/listTransactions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          email,
        },
      });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);
